import { BEHAVIOR_IFRAME_FINDER_INTERVAL } from "../utils/variables";
const sameOriginWindow = (iframeElement) => {
    try {
        const windowElement = iframeElement.contentWindow;
        return windowElement != null && windowElement.document != null ? windowElement : null;
    }
    catch (error) {
        return null;
    }
};
const iframesIn = (windowElement) => {
    try {
        return Array.from(windowElement.document.querySelectorAll("iframe"));
    }
    catch (error) {
        return [];
    }
};
class IframeBehaviorCollector {
    constructor(plasm, listeners) {
        this.hookedIframes = new WeakSet();
        this.documentByIframe = new WeakMap();
        this.stopped = false;
        this.plasm = plasm;
        this.listeners = listeners;
    }
    attachListenersTo(iframeElement) {
        if (this.stopped) {
            return;
        }
        const windowElement = sameOriginWindow(iframeElement);
        if (windowElement == null) {
            return;
        }
        this.listeners.forEach((listener) => {
            windowElement.document.addEventListener(listener.type, listener.listener, true);
        });
        this.syncBehaviorsWith(iframeElement, windowElement.document);
    }
    detachListenersFrom(iframeElement) {
        const windowElement = sameOriginWindow(iframeElement);
        if (windowElement == null) {
            return;
        }
        this.listeners.forEach((listener) => {
            windowElement.document.removeEventListener(listener.type, listener.listener, true);
        });
        this.documentByIframe.delete(iframeElement);
        this.plasm.configuredBehaviors.forEach((behavior) => behavior.detachFromDocument(windowElement.document));
    }
    syncBehaviorsWith(iframeElement, documentElement) {
        const previous = this.documentByIframe.get(iframeElement);
        if (previous === documentElement) {
            return;
        }
        if (previous != null) {
            this.plasm.configuredBehaviors.forEach((behavior) => behavior.detachFromDocument(previous));
        }
        this.documentByIframe.set(iframeElement, documentElement);
        this.plasm.configuredBehaviors.forEach((behavior) => behavior.attachToDocument(documentElement));
    }
    hookLoad(iframeElement) {
        if (this.hookedIframes.has(iframeElement)) {
            return;
        }
        this.hookedIframes.add(iframeElement);
        iframeElement.addEventListener("load", () => {
            if (this.stopped) {
                return;
            }
            this.attachListenersTo(iframeElement);
            const windowElement = sameOriginWindow(iframeElement);
            if (windowElement != null) {
                this.sweepFrom(windowElement);
            }
        });
    }
    sweepFrom(windowElement) {
        if (this.stopped) {
            return;
        }
        iframesIn(windowElement).forEach((iframeElement) => {
            this.hookLoad(iframeElement);
            this.attachListenersTo(iframeElement);
            const childWindow = sameOriginWindow(iframeElement);
            if (childWindow != null) {
                this.sweepFrom(childWindow);
            }
        });
    }
    detachFrom(windowElement) {
        iframesIn(windowElement).forEach((iframeElement) => {
            const childWindow = sameOriginWindow(iframeElement);
            if (childWindow != null) {
                this.detachFrom(childWindow);
            }
            this.detachListenersFrom(iframeElement);
        });
    }
    start() {
        this.stopped = false;
        this.sweepFrom(window);
        this.sweepIntervalId = setInterval(() => this.sweepFrom(window), BEHAVIOR_IFRAME_FINDER_INTERVAL);
        this.plasm.debug("Iframe behavior collector started");
    }
    stop() {
        this.stopped = true;
        clearInterval(this.sweepIntervalId);
        this.sweepIntervalId = undefined;
        this.detachFrom(window);
        this.plasm.debug("Iframe behavior collector stopped");
    }
}
export default IframeBehaviorCollector;
